import { Camera, CheckCircle2, FileUp, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import Scanner from '../onderdelen/Scanner'
import { FoutVak, Kop, Leeg } from '../onderdelen/ui'
import { api, Fout, uploaden } from '../lib/api'
import { ga } from '../lib/router'
import { bewaarVoorkeuren, laadWerk, leesVoorkeuren, useStaat } from '../lib/staat'
import {
  ACCEPT, bundelPaginas, isFoto, keurBestand, klachtTekst, MAX_PAGINAS, moetVerkleind, scanBonId,
  soortVanBestand, verkleinFoto, type Richting,
} from '../lib/scanbestand'

/* ===========================================================================
 *  Scannen
 *
 *  Zoals Blue10: Kies bedrijf, Kies documentsoort, en dan de foto's. Wat er
 *  daarna gebeurt is wat Scannen in het dashboard doet (src/lib/bonscannen.ts,
 *  meldBonnenAan): de bladzijden worden één bestand, gaan naar de opslag via
 *  een plek die bon-scannen uitdeelt, er komt een factuur van, en de
 *  AI-assistent leest hem. Het verkleinen en de PDF komen uit dezelfde code
 *  (lib/scanbestand.ts, een letterlijke kopie).
 *
 *  Pakbon staat er niet bij: een factuur die we niet kennen als soort zou
 *  een factuur worden, en dat is hem niet.
 * =========================================================================== */

type Stap = 'bedrijf' | 'soort' | 'paginas' | 'versturen' | 'klaar'

export default function Scannen() {
  const ik = useStaat((s) => s.ik)
  const [stap, setStap] = useState<Stap>('bedrijf')
  const [bedrijf, setBedrijf] = useState<string>('')
  const [richting, setRichting] = useState<Richting | null>(null)
  const [paginas, setPaginas] = useState<File[]>([])
  /* Foto's die nog door de scanner moeten (hoeken, rechttrekken, nakijken). */
  const [teScannen, setTeScannen] = useState<File[]>([])
  const [voortgang, setVoortgang] = useState('')
  const [fout, setFout] = useState<string | null>(null)
  const [klaar, setKlaar] = useState<{ id: string; lezen: string; bedrijf: boolean } | null>(null)
  const foto = useRef<HTMLInputElement>(null)
  const bestand = useRef<HTMLInputElement>(null)

  /* Het bedrijf van de vorige keer bovenaan: wie op één vestiging werkt, scant
     meestal voor dezelfde bv. */
  const voorkeur = leesVoorkeuren().bedrijf
  const bedrijven = useMemo(() => {
    const alle = ik?.bedrijven ?? []
    const eerst = alle.filter((b) => b.code === voorkeur)
    return [...eerst, ...alle.filter((b) => b.code !== voorkeur)]
  }, [ik, voorkeur])

  if (ik && !ik.mag.scannen) {
    return (
      <div className="scherm">
        <Kop terugNaar="/" terugLabel="Dashboard" titel="Scannen" />
        <Leeg>Scannen staat voor jouw account niet aan. Daarvoor is het recht "Factuur laten lezen" nodig.</Leeg>
      </div>
    )
  }

  function voegToe(lijst: FileList | null) {
    if (!lijst) return
    setFout(null)
    const nieuw = [...paginas]
    for (const f of Array.from(lijst)) {
      const klacht = keurBestand(f)
      if (klacht) { setFout(`"${f.name}": ${klachtTekst(klacht)}`); continue }
      /* Een PDF is al een heel stuk; die kan niet tussen foto's in. */
      const pdf = soortVanBestand(f) === 'application/pdf'
      if (pdf && nieuw.length > 0) { setFout('Een PDF moet een losse scan zijn. Verstuur de foto\'s eerst, of begin opnieuw.'); continue }
      if (!pdf && nieuw.some((x) => soortVanBestand(x) === 'application/pdf')) { setFout('Er staat al een PDF klaar; daar kunnen geen foto\'s bij.'); continue }
      if (nieuw.length + teScannen.length + (pdf ? 0 : 1) > MAX_PAGINAS) { setFout(`Hoogstens ${MAX_PAGINAS} bladzijden per factuur.`); break }
      /*
       * Een foto gaat eerst door de scanner: het vel zoeken, rechttrekken,
       * de schaduw eruit en nakijken of het leesbaar is. Een PDF is al een
       * stuk en gaat zoals hij is.
       */
      if (pdf) nieuw.push(f)
      else setTeScannen((oud) => [...oud, f])
    }
    setPaginas(nieuw)
  }

  async function verstuur() {
    if (!richting || paginas.length === 0) return
    setStap('versturen')
    setFout(null)
    const bonId = scanBonId()
    try {
      setVoortgang('Foto\'s klaarmaken…')
      const klaargemaakt: File[] = []
      for (const p of paginas) klaargemaakt.push(isFoto(p) && moetVerkleind(p) ? await verkleinFoto(p) : p)
      const stuk = await bundelPaginas(klaargemaakt, bonId)
      const klacht = keurBestand(stuk)
      if (klacht) throw new Error(klachtTekst(klacht))
      const mime = soortVanBestand(stuk)

      setVoortgang('Plek vragen…')
      const plek = await api.scanPlek({ bonId, naam: stuk.name, mime, grootte: stuk.size })

      setVoortgang('Versturen…')
      await uploaden(plek.pad, plek.token, new Blob([stuk], { type: mime }))

      setVoortgang('Factuur aanmaken…')
      const uit = await api.scanBon({ bonId, naam: stuk.name, paginas: paginas.length, richting, bedrijf })

      if (bedrijf) bewaarVoorkeuren({ ...leesVoorkeuren(), bedrijf })
      setKlaar({
        id: uit.id,
        bedrijf: uit.bedrijfGezet,
        lezen: uit.lezen.ok
          ? 'De AI-assistent leest hem nu. Daarna staat hij bij Valideren.'
          : `Hij staat erin, maar het lezen startte niet${uit.lezen.reden ? `: ${uit.lezen.reden}` : '.'} Vul hem dan zelf aan in het dashboard.`,
      })
      setStap('klaar')
      void laadWerk()
    } catch (e) {
      setFout(e instanceof Fout || e instanceof Error ? e.message : String(e))
      setStap('paginas')
    } finally {
      setVoortgang('')
    }
  }

  function opnieuw() {
    setPaginas([]); setKlaar(null); setFout(null); setStap('soort')
  }

  /* De twee verborgen kiezers staan in elk scherm waar ze geopend kunnen
     worden: ook in de scanner, want "Opnieuw" opent de camera meteen. */
  const invoer = (
    <>
      <input ref={foto} type="file" accept="image/*" capture="environment" hidden
        onChange={(e) => { voegToe(e.target.files); e.target.value = '' }} />
      <input ref={bestand} type="file" accept={ACCEPT} multiple hidden
        onChange={(e) => { voegToe(e.target.files); e.target.value = '' }} />
    </>
  )

  /* ---------------- De scanner, één foto tegelijk ---------------- */
  if (teScannen.length > 0) {
    return (
      <>
      {invoer}
      <Scanner
        key={`${teScannen[0]!.name}-${teScannen[0]!.lastModified}-${paginas.length}`}
        bestand={teScannen[0]!}
        nummer={paginas.length + 1}
        klaar={(scan) => { setPaginas((p) => [...p, scan]); setTeScannen((q) => q.slice(1)) }}
        opnieuw={() => {
          setTeScannen((q) => q.slice(1))
          /* Meteen de camera weer open, in dezelfde tik (anders weigert iOS). */
          foto.current?.click()
        }}
      />
      </>
    )
  }

  /* ---------------- Kies bedrijf ---------------- */
  if (stap === 'bedrijf') {
    return (
      <div className="scherm">
        <Kop terugNaar="/" terugLabel="Dashboard" titel="Kies bedrijf" />
        <ul className="lijst">
          <li>
            <button className="lijst-rij" onClick={() => { setBedrijf(''); setStap('soort') }}>
              <span className="kiezer-tekst"><span>Laat de AI-assistent kiezen</span><span className="klein zacht">Op de naam op de factuur; je kijkt het na bij valideren</span></span>
            </button>
          </li>
          {bedrijven.map((b) => (
            <li key={b.code}>
              <button className="lijst-rij" onClick={() => { setBedrijf(b.code); setStap('soort') }}>{b.naam}</button>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  /* ---------------- Kies documentsoort ---------------- */
  if (stap === 'soort') {
    return (
      <div className="scherm">
        <Kop titel="Kies documentsoort" terugLabel="Kies bedrijf" opTerug={() => setStap('bedrijf')} />
        <ul className="lijst">
          <li><button className="lijst-rij" onClick={() => { setRichting('inkoop'); setStap('paginas') }}>Inkoopfactuur</button></li>
          <li><button className="lijst-rij" onClick={() => { setRichting('verkoop'); setStap('paginas') }}>Verkoopfactuur</button></li>
        </ul>
      </div>
    )
  }

  /* ---------------- Klaar ---------------- */
  if (stap === 'klaar' && klaar) {
    return (
      <div className="scherm">
        <Kop terugNaar="/" terugLabel="Dashboard" titel="Verstuurd" />
        <div className="klaar-vak">
          <CheckCircle2 size={64} className="groen" aria-hidden="true" />
          <h2>{richting === 'verkoop' ? 'Verkoopfactuur' : 'Inkoopfactuur'} verstuurd</h2>
          <p>{klaar.lezen}</p>
          {bedrijf && !klaar.bedrijf && <p className="klein zacht">Het bedrijf kon niet worden vastgezet; kies het bij valideren.</p>}
        </div>
        <div className="voet-knop kolom">
          <button className="knop breed blauw groot" onClick={opnieuw}>Nog een factuur scannen</button>
          <button className="knop breed lucht" onClick={() => ga(`/factuur/${encodeURIComponent(klaar.id)}`)}>Bekijk de factuur</button>
        </div>
      </div>
    )
  }

  /* ---------------- De bladzijden ---------------- */
  const bezig = stap === 'versturen'
  return (
    <div className="scherm">
      <Kop titel={richting === 'verkoop' ? 'Verkoopfactuur' : 'Inkoopfactuur'} terugLabel="Soort"
        opTerug={() => !bezig && setStap('soort')} />

      {invoer}

      <div className="scan-knoppen">
        <button className="tegel" onClick={() => foto.current?.click()} disabled={bezig}>
          <span className="tegel-vlak tegel-lucht klein-vlak"><Camera size={56} strokeWidth={1.6} /></span>
          <span className="tegel-label">{paginas.length ? 'Nog een bladzijde' : 'Foto maken'}</span>
        </button>
        <button className="tegel" onClick={() => bestand.current?.click()} disabled={bezig}>
          <span className="tegel-vlak tegel-grijs klein-vlak"><FileUp size={52} strokeWidth={1.6} /></span>
          <span className="tegel-label">Bestand kiezen</span>
        </button>
      </div>

      {fout && <FoutVak tekst={fout} />}

      {paginas.length > 0 && (
        <ul className="lijst">
          {paginas.map((p, i) => (
            <li key={`${p.name}-${i}`} className="lijst-rij pagina-rij">
              <Duim bestand={p} />
              <span className="kiezer-tekst">
                <span>Bladzijde {i + 1}</span>
                <span className="klein zacht">{Math.max(1, Math.round(p.size / 1024)).toLocaleString('nl-NL')} kB</span>
              </span>
              <button className="kop-knop" aria-label={`Bladzijde ${i + 1} weghalen`} disabled={bezig}
                onClick={() => setPaginas(paginas.filter((_, j) => j !== i))}>
                <Trash2 size={20} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="voet-knop">
        <button className="knop breed blauw groot" disabled={bezig || paginas.length === 0} onClick={() => void verstuur()}>
          {bezig ? voortgang || 'Bezig…' : paginas.length > 1 ? `Versturen (${paginas.length} bladzijden)` : 'Versturen'}
        </button>
      </div>
    </div>
  )
}

function Duim({ bestand }: { bestand: File }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!bestand.type.startsWith('image/')) return
    const u = URL.createObjectURL(bestand)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [bestand])
  return url ? <img className="duim" src={url} alt="" /> : <span className="duim duim-pdf">PDF</span>
}
